import {
  useQuery,
  type UseQueryOptions,
  type UseQueryResult,
  type QueryKey
} from '@tanstack/react-query';
import {
  searchCategoriesAdvanced,
  getCategoryCard,
  getCategoryPrintResults,
  getBucketStatistics,
  isRPCError
} from '../services/rpc';
import type {
  BucketStatisticsResult
} from '../services/rpc';
import type {
  CategorySearchFilters,
  CategorySearchResults,
  CategoryCardPayload,
  CategoryPrintRow,
} from '../types/rpc-contracts';
import { RPC_QUERY_KEYS, RPC_CACHE_CONFIG } from '../types/rpc-contracts';

type CategorySearchKey = readonly ['lovabletsaward', 'categories', 'search', CategorySearchFilters];
type CategoryCardKey = readonly ['lovabletsaward', 'categories', 'card', string];
type CategoryPrintKey = readonly ['lovabletsaward', 'categories', 'print', string];
type BucketStatsKey = readonly ['lovabletsaward', 'categories', 'bucket-stats'];

type QueryOptions<TData, TKey extends QueryKey> = Omit<
  UseQueryOptions<TData, Error, TData, TKey>,
  'queryKey' | 'queryFn'
>;

export function useCategoriesSearch(
  filters: CategorySearchFilters,
  options?: QueryOptions<CategorySearchResults, CategorySearchKey>
): UseQueryResult<CategorySearchResults, Error> {
  return useQuery<CategorySearchResults, Error, CategorySearchResults, CategorySearchKey>({
    queryKey: RPC_QUERY_KEYS.categories.search(filters),
    queryFn: async () => {
      return await searchCategoriesAdvanced(filters);
    },
    enabled: Object.keys(filters || {}).length > 0,
    staleTime: RPC_CACHE_CONFIG.staleTime.search,
    gcTime: RPC_CACHE_CONFIG.cacheTime.search,
    retry: (failureCount: number, error: unknown) => {
      if (isRPCError(error) && error.rpcError.code.startsWith('INVALID_')) {
        return false;
      }
      return failureCount < 2;
    },
    retryDelay: 1500,
    ...options,
  });
}

export function useCategoryCard(
  categoryId: string | null,
  options?: QueryOptions<CategoryCardPayload, CategoryCardKey>
): UseQueryResult<CategoryCardPayload, Error> {
  return useQuery<CategoryCardPayload, Error, CategoryCardPayload, CategoryCardKey>({
    queryKey: RPC_QUERY_KEYS.categories.card(categoryId ?? ''),
    queryFn: async () => {
      if (!categoryId) throw new Error('Missing category id');
      return await getCategoryCard(categoryId);
    },
    enabled: Boolean(categoryId),
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
    retry: 1,
    ...options,
  });
}

export function useCategoryPrintResults(
  jobId: string | null,
  options?: QueryOptions<CategoryPrintRow[], CategoryPrintKey>
): UseQueryResult<CategoryPrintRow[], Error> {
  return useQuery<CategoryPrintRow[], Error, CategoryPrintRow[], CategoryPrintKey>({
    queryKey: RPC_QUERY_KEYS.categories.print(jobId ?? ''),
    queryFn: async () => {
      if (!jobId) throw new Error('Missing job id');
      return await getCategoryPrintResults(jobId);
    },
    enabled: Boolean(jobId),
    staleTime: 0,
    gcTime: 5 * 60 * 1000,
    retry: 1,
    ...options,
  });
}

export function useBucketStatistics(
  options?: QueryOptions<BucketStatisticsResult, BucketStatsKey>
): UseQueryResult<BucketStatisticsResult, Error> {
  return useQuery<BucketStatisticsResult, Error, BucketStatisticsResult, BucketStatsKey>({
    queryKey: ['lovabletsaward', 'categories', 'bucket-stats'] as const,
    queryFn: async () => {
      return await getBucketStatistics();
    },
    staleTime: 5 * 60 * 1000, // 5 minutes - stats don't change frequently
    gcTime: 30 * 60 * 1000, // 30 minutes
    retry: 2,
    ...options,
  });
}
